(() => {
  const ROOT_SELECTOR = '[data-bis-root]';
  const instances = new WeakMap();

  const normalize = (value = '') =>
    String(value)
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/\*/g, '')
      .replace(/\s+/g, ' ')
      .trim()
      .toLowerCase();

  const escapeCss = (value) => {
    if (window.CSS && typeof window.CSS.escape === 'function') {
      return window.CSS.escape(value);
    }

    return String(value).replace(/["\\]/g, '\\$&');
  };

  class BackInStock {
    constructor(root) {
      this.root = root;
      this.modal = root.querySelector('[data-bis-dialog]');
      this.trigger = root.querySelector('[data-bis-trigger]');
      this.closeButton = root.querySelector('[data-bis-close]');
      this.formHost = root.querySelector('[data-bis-form-host]');

      this.enabled = root.dataset.enabled === 'true';
      this.hasForm = root.dataset.hasForm === 'true';
      this.designMode = root.dataset.designMode === 'true';
      this.debug = root.dataset.debug === 'true';

      this.productTitle = root.dataset.productTitle || '';
      this.productUrl = root.dataset.productUrl || window.location.pathname;
      this.initialVariantId = String(root.dataset.initialVariantId || '');

      this.productFormSelector =
        root.dataset.productFormSelector || "form[action*='/cart/add']";

      this.buyButtonSelector =
        root.dataset.buyButtonSelector ||
        "button[name='add'], .product-form__submit, [data-add-to-cart]";

      const rawProductUrlField = (root.dataset.fieldProductUrl || '').trim();
      const ignoredOptionalLabels = new Set(['http://', 'https://', 'http:', 'https:']);

      this.fieldLabels = {
        product: root.dataset.fieldProduct || '',
        variant: root.dataset.fieldVariant || '',
        variantId: root.dataset.fieldVariantId || '',
        productUrl: ignoredOptionalLabels.has(rawProductUrlField.toLowerCase())
          ? ''
          : rawProductUrlField,
      };

      this.singleVariantLabel =
        (root.dataset.singleVariantLabel || '').trim() || 'Variante única';

      this.variants = this.readVariants();
      this.variantIds = new Set(this.variants.map((variant) => String(variant.id)));
      this.currentVariant = null;
      this.currentNativeButton = null;
      this.nativeButtonState = new WeakMap();
      this.lastVariantId = null;
      this.fillInterval = null;
      this.watchInterval = null;
      this.formObserver = null;
      this.destroyed = false;

      this.onTriggerClick = this.onTriggerClick.bind(this);
      this.onCloseClick = this.onCloseClick.bind(this);
      this.onDialogClick = this.onDialogClick.bind(this);
      this.onEscapeKey = this.onEscapeKey.bind(this);
      this.onDocumentVariantEvent = this.onDocumentVariantEvent.bind(this);
      this.onSectionSelect = this.onSectionSelect.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);

      this.init();
    }

    log(...args) {
      if (this.debug) {
        console.info('[Avise-me]', ...args);
      }
    }

    warn(...args) {
      console.warn('[Avise-me]', ...args);
    }

    readVariants() {
      const script = this.root.querySelector('[data-bis-variants]');

      if (!script) return [];

      try {
        const data = JSON.parse(script.textContent || '[]');
        return Array.isArray(data) ? data : [];
      } catch (error) {
        this.warn('Não foi possível ler as variantes do produto.', error);
        return [];
      }
    }

    init() {
      if (!this.enabled) {
        this.trigger.hidden = true;
        return;
      }

      if (!this.hasForm && !this.designMode) {
        this.trigger.hidden = true;
        this.warn(
          'Nenhum bloco de app foi adicionado à seção. Adicione o Shopify Forms dentro da seção Avise-me.'
        );
        return;
      }

      this.trigger.addEventListener('click', this.onTriggerClick);
      this.closeButton?.addEventListener('click', this.onCloseClick);
      this.modal?.addEventListener('click', this.onDialogClick);
      document.addEventListener('keydown', this.onEscapeKey);

      document.addEventListener('variant:change', this.onDocumentVariantEvent);
      document.addEventListener('variant-change', this.onDocumentVariantEvent);
      document.addEventListener('product:variant-change', this.onDocumentVariantEvent);
      document.addEventListener('change', this.onDocumentVariantEvent, true);
      document.addEventListener('input', this.onDocumentVariantEvent, true);

      document.addEventListener('shopify:section:select', this.onSectionSelect);
      document.addEventListener('shopify:block:select', this.onBlockSelect);

      this.observeFormHost();
      this.update();

      this.watchInterval = window.setInterval(() => {
        if (this.destroyed) return;
        this.update();
      }, 500);
    }

    destroy() {
      this.destroyed = true;

      this.trigger?.removeEventListener('click', this.onTriggerClick);
      this.closeButton?.removeEventListener('click', this.onCloseClick);
      this.modal?.removeEventListener('click', this.onDialogClick);
      document.removeEventListener('keydown', this.onEscapeKey);

      document.removeEventListener('variant:change', this.onDocumentVariantEvent);
      document.removeEventListener('variant-change', this.onDocumentVariantEvent);
      document.removeEventListener('product:variant-change', this.onDocumentVariantEvent);
      document.removeEventListener('change', this.onDocumentVariantEvent, true);
      document.removeEventListener('input', this.onDocumentVariantEvent, true);

      document.removeEventListener('shopify:section:select', this.onSectionSelect);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);

      window.clearInterval(this.watchInterval);
      window.clearInterval(this.fillInterval);
      this.formObserver?.disconnect();

      this.restoreNativeButton();
      document.documentElement.classList.remove('bis-scroll-locked');
    }

    findProductForm() {
      let forms = [];

      try {
        forms = Array.from(document.querySelectorAll(this.productFormSelector));
      } catch (error) {
        this.warn('Seletor do formulário de produto inválido:', this.productFormSelector);
        return null;
      }

      if (!forms.length) return null;

      /*
       * Alguns temas renderizam mais de um form[action*="/cart/add"] na PDP.
       * Um deles pode ser somente o formulário de parcelamento/installments
       * e também conter input[name="id"].
       *
       * Por isso, priorizamos explicitamente formulários que também tenham
       * o botão de compra configurado em buyButtonSelector.
       */
      const formsWithBuyButton = forms.filter((form) => Boolean(this.findBuyButton(form)));

      const matchingVariantForm = formsWithBuyButton.find((form) => {
        const input = form.querySelector("input[name='id']");
        return input && this.variantIds.has(String(input.value));
      });

      if (matchingVariantForm) return matchingVariantForm;

      const visibleBuyForm = formsWithBuyButton.find((form) => {
        const rect = form.getBoundingClientRect();
        return rect.width > 0 && rect.height > 0;
      });

      if (visibleBuyForm) return visibleBuyForm;

      if (formsWithBuyButton.length) return formsWithBuyButton[0];

      this.log(
        'Foram encontrados formulários /cart/add, mas nenhum contém o botão de compra com o seletor configurado.',
        forms
      );

      return null;
    }

    findBuyButton(form) {
      if (!form) return null;

      try {
        const inside = form.querySelector(this.buyButtonSelector);
        if (inside) return inside;
      } catch (error) {
        this.warn('Seletor do botão Comprar inválido:', this.buyButtonSelector);
        return null;
      }

      if (form.id) {
        const formSelector = `[form="${escapeCss(form.id)}"]`;
        const candidates = Array.from(document.querySelectorAll(formSelector));

        const external = candidates.find((element) => {
          try {
            return element.matches(this.buyButtonSelector);
          } catch (_) {
            return false;
          }
        });

        if (external) return external;
      }

      return null;
    }

    getVariantId(form) {
      const formVariantInput = form?.querySelector("input[name='id']");

      if (formVariantInput?.value && this.variantIds.has(String(formVariantInput.value))) {
        return String(formVariantInput.value);
      }

      const urlVariant = new URL(window.location.href).searchParams.get('variant');

      if (urlVariant && this.variantIds.has(String(urlVariant))) {
        return String(urlVariant);
      }

      if (this.initialVariantId && this.variantIds.has(this.initialVariantId)) {
        return this.initialVariantId;
      }

      return null;
    }

    getVariant(id) {
      if (!id) return null;
      return this.variants.find((variant) => String(variant.id) === String(id)) || null;
    }

    rememberNativeButton(button) {
      if (!button || this.nativeButtonState.has(button)) return;

      this.nativeButtonState.set(button, {
        inlineDisplay: button.style.display,
        hidden: button.hidden,
      });
    }

    hideNativeButton(button) {
      if (!button) return;

      this.rememberNativeButton(button);
      button.hidden = true;
      button.style.setProperty('display', 'none', 'important');
      button.setAttribute('data-bis-native-hidden', 'true');
    }

    restoreButton(button) {
      if (!button) return;

      const state = this.nativeButtonState.get(button);

      if (state) {
        button.hidden = state.hidden;

        if (state.inlineDisplay) {
          button.style.display = state.inlineDisplay;
        } else {
          button.style.removeProperty('display');
        }
      } else {
        button.hidden = false;
        button.style.removeProperty('display');
      }

      button.removeAttribute('data-bis-native-hidden');
    }

    restoreNativeButton() {
      if (this.currentNativeButton) {
        this.restoreButton(this.currentNativeButton);
      }
    }

    copyNativeButtonClasses(button) {
      if (!button || !this.trigger) return;

      /*
       * O trigger é movido para junto do botão nativo.
       * Como ele deixa de ser descendente de .bis-root, precisamos copiar
       * as CSS custom properties da section diretamente para o botão.
       */
      this.trigger.className = 'bis-trigger';
      this.trigger.setAttribute('type', 'button');
      this.trigger.removeAttribute('disabled');
      this.trigger.removeAttribute('aria-disabled');
      this.trigger.removeAttribute('style');

      const rootStyles = window.getComputedStyle(this.root);
      [
        '--bis-button-bg',
        '--bis-button-color',
        '--bis-button-border',
        '--bis-button-radius',
        '--bis-button-height',
      ].forEach((property) => {
        const value = rootStyles.getPropertyValue(property).trim();
        if (value) {
          this.trigger.style.setProperty(property, value);
        }
      });
    }

    mountTrigger(button) {
      if (!button || !this.trigger) return;

      if (this.currentNativeButton && this.currentNativeButton !== button) {
        this.restoreButton(this.currentNativeButton);
      }

      this.currentNativeButton = button;
      this.rememberNativeButton(button);
      this.copyNativeButtonClasses(button);

      if (this.trigger.previousElementSibling !== button) {
        button.insertAdjacentElement('afterend', this.trigger);
      }
    }


    update() {
      if (!this.enabled || this.destroyed) return;

      const form = this.findProductForm();
      const button = this.findBuyButton(form);

      if (!form || !button) {
        this.trigger.hidden = true;
        return;
      }

      this.mountTrigger(button);

      const variantId = this.getVariantId(form);
      const variant = this.getVariant(variantId);

      if (!variant) {
        this.currentVariant = null;
        this.trigger.hidden = true;
        this.restoreButton(button);
        return;
      }

      this.currentVariant = variant;

      if (this.lastVariantId !== String(variant.id)) {
        this.log('Variante atual:', variant);
        this.lastVariantId = String(variant.id);
      }

      if (variant.available) {
        this.trigger.hidden = true;
        this.restoreButton(button);
        return;
      }

      this.hideNativeButton(button);
      this.trigger.hidden = false;
    }

    onDocumentVariantEvent(event) {
      if (this.destroyed) return;

      if (event?.detail?.variant === null) {
        this.currentVariant = null;
      }

      window.requestAnimationFrame(() => this.update());
    }

    onTriggerClick() {
      this.update();

      if (!this.currentVariant || this.currentVariant.available) {
        return;
      }

      this.openModal();
    }

    openModal() {
      if (!this.modal) return;

      /*
       * IMPORTANTE:
       * O Shopify Forms inline precisa existir em uma árvore renderizável
       * durante a inicialização. O antigo <dialog> fechado aplica display:none
       * nativamente e pode impedir o Forms de montar o <form-embed>.
       *
       * Agora o modal permanece montado o tempo todo e é apenas tornado
       * interativo/visível quando abre.
       */
      this.modal.classList.add('is-open');
      this.modal.removeAttribute('inert');
      this.modal.setAttribute('aria-hidden', 'false');

      document.documentElement.classList.add('bis-scroll-locked');

      // Nudge para componentes que recalculam layout quando a viewport muda.
      window.dispatchEvent(new Event('resize'));

      this.startAutofill();

      window.setTimeout(() => {
        this.fillAutomaticFields();
        const firstField = this.findFirstVisibleInput();
        firstField?.focus({ preventScroll: true });
      }, 180);
    }

    closeModal() {
      if (!this.modal) return;

      this.modal.classList.remove('is-open');
      this.modal.setAttribute('inert', '');
      this.modal.setAttribute('aria-hidden', 'true');

      document.documentElement.classList.remove('bis-scroll-locked');
      this.stopAutofill();
      this.trigger?.focus({ preventScroll: true });
    }

    onCloseClick() {
      this.closeModal();
    }

    onDialogClick(event) {
      if (event.target?.matches?.('[data-bis-backdrop]')) {
        this.closeModal();
      }
    }

    onEscapeKey(event) {
      if (event.key === 'Escape' && this.isModalOpen()) {
        event.preventDefault();
        this.closeModal();
      }
    }

    onSectionSelect(event) {
      if (!this.designMode) return;

      const sectionId = event?.detail?.sectionId;
      if (sectionId && String(sectionId) === String(this.root.dataset.sectionId)) {
        this.openModal();
      }
    }

    onBlockSelect(event) {
      if (!this.designMode) return;

      if (this.root.contains(event.target)) {
        this.openModal();
      }
    }

    observeFormHost() {
      if (!this.formHost || typeof MutationObserver === 'undefined') return;

      this.formObserver = new MutationObserver(() => {
        if (this.isModalOpen()) {
          this.fillAutomaticFields();
        }
      });

      this.formObserver.observe(this.formHost, {
        childList: true,
        subtree: true,
      });
    }

    isModalOpen() {
      return Boolean(this.modal?.classList.contains('is-open'));
    }

    startAutofill() {
      this.stopAutofill();
      this.fillAutomaticFields();

      this.fillInterval = window.setInterval(() => {
        if (!this.isModalOpen()) {
          this.stopAutofill();
          return;
        }

        this.fillAutomaticFields();
      }, 1000);
    }

    stopAutofill() {
      if (this.fillInterval) {
        window.clearInterval(this.fillInterval);
        this.fillInterval = null;
      }
    }

    collectRoots(start) {
      if (!start) return [];

      const roots = [];
      const queue = [start];
      const seen = new Set();

      while (queue.length) {
        const current = queue.shift();

        if (!current || seen.has(current)) continue;

        seen.add(current);
        roots.push(current);

        if (current.querySelectorAll) {
          current.querySelectorAll('*').forEach((element) => {
            if (element.shadowRoot && !seen.has(element.shadowRoot)) {
              queue.push(element.shadowRoot);
            }
          });
        }
      }

      return roots;
    }

    getControlFromLabel(root, label) {
      if (!label) return null;

      if (label.control) return label.control;

      const forId = label.getAttribute?.('for');

      if (forId) {
        if (typeof root.getElementById === 'function') {
          const byId = root.getElementById(forId);
          if (byId) return byId;
        }

        const bySelector = root.querySelector?.(`#${escapeCss(forId)}`);
        if (bySelector) return bySelector;
      }

      return label.querySelector?.('input, textarea, select') || null;
    }

    findControlByLabel(labelText) {
      const wanted = normalize(labelText);

      if (!wanted || !this.formHost) return null;

      const roots = this.collectRoots(this.formHost);

      for (const root of roots) {
        const labels = Array.from(root.querySelectorAll?.('label') || []);

        const matchingLabel = labels.find((label) => {
          const actual = normalize(label.textContent);
          return actual === wanted || actual.startsWith(`${wanted} `);
        });

        if (matchingLabel) {
          const control = this.getControlFromLabel(root, matchingLabel);
          if (control) return { control, label: matchingLabel };
        }

        const attributed = Array.from(
          root.querySelectorAll?.(
            'input[aria-label], textarea[aria-label], select[aria-label], input[placeholder], textarea[placeholder], [label]'
          ) || []
        );

        for (const element of attributed) {
          const candidates = [
            element.getAttribute?.('aria-label'),
            element.getAttribute?.('placeholder'),
            element.getAttribute?.('label'),
          ]
            .filter(Boolean)
            .map(normalize);

          if (!candidates.includes(wanted)) continue;

          if (element.matches?.('input, textarea, select')) {
            return { control: element, label: null };
          }

          const inner =
            element.shadowRoot?.querySelector?.('input, textarea, select') ||
            element.querySelector?.('input, textarea, select');

          if (inner) return { control: inner, label: element };
        }

        const textCandidates = Array.from(root.querySelectorAll?.('div, span, p') || [])
          .filter((element) => normalize(element.textContent) === wanted)
          .sort((a, b) => a.textContent.length - b.textContent.length);

        for (const textElement of textCandidates) {
          let parent = textElement.parentElement;

          for (let i = 0; parent && i < 4; i += 1, parent = parent.parentElement) {
            const control = parent.querySelector?.('input, textarea, select');
            if (control) return { control, label: textElement };
          }
        }
      }

      return null;
    }

    setControlValue(control, value) {
      if (!control) return false;

      const stringValue = value == null ? '' : String(value);

      try {
        let prototype = control;
        let descriptor = null;

        while (prototype && !descriptor) {
          descriptor = Object.getOwnPropertyDescriptor(prototype, 'value');
          prototype = Object.getPrototypeOf(prototype);
        }

        if (descriptor?.set) {
          descriptor.set.call(control, stringValue);
        } else {
          control.value = stringValue;
        }

        control.setAttribute?.('value', stringValue);

        control.dispatchEvent(
          new Event('input', {
            bubbles: true,
            composed: true,
          })
        );

        control.dispatchEvent(
          new Event('change', {
            bubbles: true,
            composed: true,
          })
        );

        return true;
      } catch (error) {
        this.warn('Falha ao preencher campo automático.', error);
        return false;
      }
    }

    getAutomaticFieldContainer(control, labelElement) {
      if (!control) return null;

      /*
       * Shopify Forms pode renderizar cada campo em estruturas diferentes
       * (inclusive Shadow DOM). Em vez de depender do texto/label para achar
       * o wrapper, subimos na árvore enquanto o ancestral ainda contém apenas
       * este único controle. Assim escondemos a "linha" inteira do campo,
       * sem correr o risco de esconder o formulário todo.
       */
      let candidate =
        control.closest?.('label') ||
        labelElement?.closest?.('label') ||
        control.parentElement ||
        control;

      let node = candidate;

      for (let depth = 0; node && depth < 7; depth += 1) {
        const parent = node.parentElement;
        if (!parent) break;

        if (
          parent.matches?.(
            'form, form-embed, [data-form-root="true"], [data-forms-id], .bis-form-host'
          )
        ) {
          break;
        }

        const controls = parent.querySelectorAll?.(
          'input:not([type="hidden"]), textarea, select'
        );

        if (!controls || controls.length !== 1) {
          break;
        }

        candidate = parent;
        node = parent;
      }

      /*
       * Se o input estiver dentro do Shadow DOM de um componente de CAMPO,
       * podemos esconder o host desse componente. Nunca escondemos o
       * <form-embed>, pois ele contém o formulário inteiro.
       */
      const rootNode = control.getRootNode?.();

      if (
        rootNode instanceof ShadowRoot &&
        rootNode.host &&
        rootNode.host.tagName?.toLowerCase() !== 'form-embed'
      ) {
        const host = rootNode.host;
        const hostName = host.tagName?.toLowerCase() || '';

        if (
          !hostName.includes('form') &&
          !host.matches?.('[data-form-root="true"], [data-forms-id]')
        ) {
          candidate = host;
        }
      }

      return candidate || control;
    }

    hideAutomaticField(control, labelElement, labelText) {
      if (!control) return;

      const target = this.getAutomaticFieldContainer(control, labelElement);

      [control, target].filter(Boolean).forEach((element) => {
        element.setAttribute?.('data-bis-auto-field', 'true');
        element.setAttribute?.('aria-hidden', 'true');
        element.style?.setProperty('display', 'none', 'important');
        element.style?.setProperty('visibility', 'hidden', 'important');
        element.style?.setProperty('height', '0', 'important');
        element.style?.setProperty('min-height', '0', 'important');
        element.style?.setProperty('margin', '0', 'important');
        element.style?.setProperty('padding', '0', 'important');
        element.style?.setProperty('border', '0', 'important');
      });

      /*
       * Não usamos disabled: campo disabled não é enviado no submit.
       * hidden/display:none mantém o valor disponível para submissão.
       */
      control.hidden = true;
      control.tabIndex = -1;
      control.setAttribute?.('readonly', '');
      control.setAttribute?.('data-bis-automatic-input', 'true');

      this.log(`Campo automático ocultado: "${labelText}"`);
    }

    fillField(labelText, value) {
      if (!labelText) return true;

      const apply = () => {
        const found = this.findControlByLabel(labelText);

        if (!found) {
          return false;
        }

        const success = this.setControlValue(found.control, value);

        if (success) {
          this.hideAutomaticField(found.control, found.label, labelText);
        }

        return success;
      };

      const success = apply();

      if (!success) {
        this.log(`Campo ainda não encontrado: "${labelText}"`);
        return false;
      }

      /*
       * Shopify Forms pode re-renderizar o campo depois de input/change.
       * Reaplicamos a ocultação após o ciclo de render para impedir que
       * os campos técnicos voltem a aparecer para o cliente.
       */
      window.setTimeout(apply, 0);
      window.setTimeout(apply, 80);
      window.setTimeout(apply, 250);

      return true;
    }

    getVariantDisplayTitle(variant) {
      if (!variant) return '';

      const rawTitle = String(variant.title || '').trim();
      const isSingleVariantProduct = this.variants.length === 1;
      const hasTechnicalDefaultTitle =
        !rawTitle || normalize(rawTitle) === normalize('Default Title');

      if (isSingleVariantProduct && hasTechnicalDefaultTitle) {
        return this.singleVariantLabel;
      }

      return rawTitle;
    }

    fillAutomaticFields() {
      const variant = this.currentVariant;

      if (!variant) return;

      const variantDisplayTitle = this.getVariantDisplayTitle(variant);

      const results = {
        product: this.fillField(this.fieldLabels.product, this.productTitle),
        variant: this.fillField(this.fieldLabels.variant, variantDisplayTitle),
        variantId: this.fillField(this.fieldLabels.variantId, variant.id),
      };

      if (this.fieldLabels.productUrl) {
        const absoluteUrl = new URL(this.productUrl, window.location.origin).href;
        results.productUrl = this.fillField(this.fieldLabels.productUrl, absoluteUrl);
      }

      const requiredResults = Object.entries(results).filter(([key]) => {
        if (key === 'productUrl') return Boolean(this.fieldLabels.productUrl);
        return true;
      });

      if (requiredResults.every(([, success]) => success)) {
        this.log('Campos automáticos preenchidos.', {
          produto: this.productTitle,
          variante: variantDisplayTitle,
          variantId: variant.id,
        });
      }
    }

    findFirstVisibleInput() {
      if (!this.formHost) return null;

      const roots = this.collectRoots(this.formHost);

      for (const root of roots) {
        const controls = Array.from(
          root.querySelectorAll?.('input, textarea, select, button') || []
        );

        const candidate = controls.find((control) => {
          if (control.closest?.('[data-bis-auto-field="true"]')) return false;
          if (control.disabled) return false;
          if (control.type === 'hidden') return false;

          const style = window.getComputedStyle(control);
          return style.display !== 'none' && style.visibility !== 'hidden';
        });

        if (candidate) return candidate;
      }

      return null;
    }
  }

  const initRoot = (root) => {
    if (!root || instances.has(root)) return;

    const instance = new BackInStock(root);
    instances.set(root, instance);
  };

  const initAll = (scope = document) => {
    if (scope.matches?.(ROOT_SELECTOR)) {
      initRoot(scope);
    }

    scope.querySelectorAll?.(ROOT_SELECTOR).forEach(initRoot);
  };

  const destroyWithin = (scope) => {
    if (!scope) return;

    const roots = [];

    if (scope.matches?.(ROOT_SELECTOR)) roots.push(scope);
    scope.querySelectorAll?.(ROOT_SELECTOR).forEach((root) => roots.push(root));

    roots.forEach((root) => {
      const instance = instances.get(root);
      instance?.destroy();
      instances.delete(root);
    });
  };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initAll(document), { once: true });
  } else {
    initAll(document);
  }

  document.addEventListener('shopify:section:load', (event) => {
    initAll(event.target);
  });

  document.addEventListener('shopify:section:unload', (event) => {
    destroyWithin(event.target);
  });
})();
